import React, { useEffect } from 'react';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import { useLazyQuery } from '@apollo/client';
import { CircularProgress, ThemeProvider } from '@material-ui/core';

import { Product, getTheme } from '@/utils/productHelpers';
import { getSupabaseClient } from '@/utils/supabaseClient';
import { activateSparkConfirm, requestAuthCode } from '@/utils/sparkActivationApi';
import {
  REDIRECT_TO_SESSION_KEY, PRODUCT_DEEP_LINKS,
} from '@/utils/constants';
import { isSafeRedirectUri } from '@/utils/urlHelpers';
import { ME_QUERY } from '@/apollo/queries';

/**
 * Extract entitlement status from a Supabase JWT.
 * Returns 'active', 'pending', or undefined if not present.
 */
const getEntitlementStatus = (accessToken: string): string | undefined => {
  try {
    const payload = JSON.parse(atob(accessToken.split('.')[1]));
    return payload.bh_claims?.products?.[0]?.status;
  } catch {
    return undefined;
  }
};

const EmailAuthCallback: React.FC = () => {
  const history = useHistory();
  const { product } = useParams<{ product: Product }>();
  const { search } = useLocation();
  const [fetchMe] = useLazyQuery<{ me: { uid: string } }>(ME_QUERY, { fetchPolicy: 'no-cache' });

  useEffect(() => {
    // Login flow: user already active — skip activation, go straight to session handoff.
    const handleLoginFlow = async () => {
      const {
        data: { session: refreshedSession },
        error: refreshError,
      } = await getSupabaseClient().auth.refreshSession();

      if (refreshError || !refreshedSession?.access_token || !refreshedSession?.refresh_token) {
        console.error('[EmailAuthCallback] refreshSession failed:', refreshError?.message);
        history.replace('/spark/login');
        return;
      }

      const redirectTo = sessionStorage.getItem(REDIRECT_TO_SESSION_KEY)
        || PRODUCT_DEEP_LINKS[product];
      if (!redirectTo || !isSafeRedirectUri(redirectTo)) {
        console.error('[EmailAuthCallback] redirectTo is missing or invalid');
        history.replace('/spark/login');
        return;
      }

      let exchangeToken: string;
      try {
        ({ exchangeToken } = await requestAuthCode(
          refreshedSession.access_token,
          refreshedSession.refresh_token,
        ));
      } catch (err) {
        console.error('[EmailAuthCallback] requestAuthCode failed:', err);
        history.replace('/spark/login');
        return;
      }

      const { data: meData } = await fetchMe({
        context: {
          headers: { Authorization: `Bearer ${refreshedSession.access_token}` },
        },
      });
      const limbixUserUid = meData?.me?.uid;

      const deepLinkUrl = new URL(redirectTo);
      deepLinkUrl.searchParams.set('exchange_token', exchangeToken);
      if (limbixUserUid) {
        deepLinkUrl.searchParams.set('limbix_user_uid', limbixUserUid);
      }

      window.location.href = deepLinkUrl.toString();
    };

    const handleCallback = async () => {
      try {
        // Wait for Supabase to process the hash fragment (detectSessionInUrl is async)
        await new Promise((resolve) => { setTimeout(resolve, 1500); });

        const { data: { session } } = await getSupabaseClient().auth.getSession();

        if (!session) {
          history.replace(`/activate/${product}/access-code`, {
            error: 'email_verification_failed',
          });
          return;
        }

        const supabaseUserId = session.user?.id ?? '';
        const patientUid = session.user?.user_metadata?.limbixUserId || '';

        const entitlementStatus = getEntitlementStatus(session.access_token);
        const rawAccessCode = session.user?.user_metadata?.accessCode;
        const accessCode = typeof rawAccessCode === 'string' && rawAccessCode.trim()
          ? rawAccessCode.trim()
          : null;

        // If access code exists in metadata, this is an activation flow.
        // After activation completes, Vercel clears accessCode from metadata.
        // So returning users won't have it → login handoff.
        const isReturningUser = !accessCode && entitlementStatus === 'active';

        if (isReturningUser) {
          await handleLoginFlow();
          return;
        }

        // Activation flow: confirm entitlement in Supabase.
        // activateUserByAccessCode is called later in UserInfoConfirmation after demographics.
        try {
          await activateSparkConfirm({ supabaseUserId, accessToken: session.access_token });
        } catch (err) {
          console.error('[EmailAuthCallback] activation failed:', err);
          history.replace(`/activate/${product}/access-code`, {
            error: 'email_verification_failed',
          });
          return;
        }

        // Store redirect target for UserInfoConfirmation session handoff
        const redirectTo = PRODUCT_DEEP_LINKS[product];
        if (redirectTo && isSafeRedirectUri(redirectTo)) {
          sessionStorage.setItem(REDIRECT_TO_SESSION_KEY, redirectTo);
        }

        const orgSlug = new URLSearchParams(search).get('org_slug');

        // Employer flow: go to coverage check first, then demographics.
        // Access code flow: go directly to demographics.
        if (orgSlug) {
          history.replace(`/activate/${product}/coverage-check/${orgSlug}`, {
            patientUid,
            flow: 'email',
          });
        } else {
          history.replace(`/activate/${product}/user-info/confirmation`, {
            patientUid,
            flow: 'email',
            accessCode,
          });
        }
      } catch (err) {
        console.error('[EmailAuthCallback] Unexpected error:', err);
        history.replace(`/activate/${product}/access-code`, {
          error: 'email_verification_failed',
        });
      }
    };

    handleCallback();
  }, [history, product, fetchMe]);

  return (
    <ThemeProvider theme={getTheme(product)}>
      <div style={{
        display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh',
      }}
      >
        <CircularProgress color="primary" />
      </div>
    </ThemeProvider>
  );
};

export default EmailAuthCallback;
